import { CustomFab } from "components/buttons/CustomFab";
import { postData } from "utils/networking/postData";
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Modal,
  TextField,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useUserType } from "hooks/useUserType";
import { toast } from "react-toastify";
import TableContainer from "@mui/material/TableContainer";
import { USER_TYPES } from "consts";
import ConfirmDialog from "components/ConfirmDialog";

///////////////////////////////////////////////////////// FORM ///////////////////////////////////////////////////////////////////

interface FieldDef {
  key: string;           // key used in formData
  label: string;         // label shown to user (can have spaces)
  placeholder?: string;  // optional placeholder guide
  defaultValue?: string; // optional default value
}

export const addEntryQuery = async (
  data: Record<string, unknown>,
) => {
  try {
    const SUBMISSION_URI = "ezyvet/addclient";
    const result = await postData({
      uri: SUBMISSION_URI,
      body: data, 
    });
    if("success" in result) {
      toast.success("Client added");
    } else if(result.errorMessage) {
      toast.error(result.errorMessage);
    }
    return result;
  } catch (error) {
    toast.error(error.errorMessage);
    throw error;
  }
};

export const deleteEntryQuery = async (
  data: Record<string, unknown>,
) => {
  if(true) {
//RABBIT
  }
  try {
    const SUBMISSION_URI = "ezyvet/removeclient";
    const result = await postData({
      uri: SUBMISSION_URI,
      body: data, 
    });
    if("success" in result) {
      toast.success("Client Removed");
    } else if(result.errorMessage) {
      toast.error(result.errorMessage);
    }
    return result;
  } catch (error) {
    toast.error(error.errorMessage);
    throw error;
  }
};

///////////////////////////////////////////////////////// TABLE ///////////////////////////////////////////////////////////////////

export const feedbackDataRetrievalQuery = async (
  data: Record<string, unknown>, //PROBABLY UNNECESSARY
) => {
  if(true) {}
  try {
    const SUBMISSION_URI = "xcaliber/retrievefeedback";
    const result = await postData({
      uri: SUBMISSION_URI,
      body: data, //PROBABLY UNNECESSARY
    });

    if ('errorMessage' in result) {
        console.error(result.errorMessage);
        return;
    }
    if (!Array.isArray(result)) {
        console.error("Unexpected response format:", result);
        return;
    }
    const desiredOrder = ["institution", "xcaliber_feedback", "link", "sop_iuid"]; // pick only these, in this order
    const formattedRows = result.map(obj => {
        const newObj = {};
        desiredOrder.forEach(key => {
            newObj[key] = obj[key];
        });
        return newObj;
    });
    return formattedRows;
  } catch (error) {
    toast.error(error.errorMessage);
    throw error;
  }
};

const columnNames = {
  study_pk: "Study Pk",
  study_iuid: "Study IUID",
  sop_iuid: "Instance IUID",
  institution: "Institution",
  xcaliber_feedback: "Feedback",
  link: "Link",
};
///////////////////////////////////////////////////////// MODAL ELEMENT (COMBINED) ///////////////////////////////////////////////////////////////////

export const XcaliberDataEntry: React.FC = () => {
  type ConfirmDialogAction = "delete" | "save" | null;
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDialogAction, setConfirmDialogAction] = useState<ConfirmDialogAction>(null);
  const [confirmDialogSelectedRowIndex, setConfirmDialogSelectedRowIndex] = useState<number | null>(null);
  const fieldDefs: FieldDef[] = [
    { key: "fieldPracticeName", label: "Practice Name", placeholder: "Name of Practice as per PACS" },
    { key: "fieldAETitle", label: "AE Title", placeholder: "Ensure this is hard to guess" },
    { key: "fieldModality", label: "Modality", placeholder: "eg DX" },
    { key: "fieldClientAbbreviation", label: "Practice Abbreviation", placeholder: "Short unique identifier for the practice" },
    { key: "fieldStationName", label: "Station Name", placeholder: "As per client imaging software" },
    { key: "fieldClientId", label: "Client ID", placeholder: "Obtain from client (step 2)" },
    { key: "fieldClientSecret", label: "Client Secret", placeholder: "Obtain from client (step 2)" },
    { key: "fieldGrantType", label: "Grant Type", defaultValue: "client_credentials" },
    { key: "fieldScope", label: "Scope", defaultValue: "read-diagnosticrequest,read-diagnosticresult,read-diagnosticresultitem,read-systemsetting,write-attachment,write-diagnosticresult,write-diagnosticresultitem" },
    { key: "fieldPartnerId", label: "Partner ID", defaultValue: process.env.REACT_APP_EZYVET_PARTNER_ID },
  ];
  const [formData, setFormData] = useState<Record<string, string>>(
    fieldDefs.reduce((acc, f) => {
      acc[f.key] = f.defaultValue ?? "";
      return acc;
    }, {} as Record<string, string>)
  );

  const modalRef = useRef<HTMLDivElement>(null);

  // Close modal if clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setModalOpen(false);
      }
    };
    if (modalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalOpen]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitNewClient = async () => {
    const data = {
      practiceName: formData.fieldPracticeName,
      aeTitle: formData.fieldAETitle,
      modality: formData.fieldModality,
      clientAbbreviation: formData.fieldClientAbbreviation,
      stationName:  formData.fieldStationName,
      clientId: formData.fieldClientId,
      clientSecret: formData.fieldClientSecret,
      grantType: formData.fieldGrantType,
      scope: formData.fieldScope,
      partnerId: formData.fieldPartnerId,
    };
    const result = await addEntryQuery(data);
  };

  const { userType, userTypeColour } = useUserType();

  const svgContent = `
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900'>
      <path d='M450,-20 C300,200 100,200 -20,250' fill='none' stroke='${userTypeColour}' stroke-width='25' opacity='0.7'/>
      <path d='M250,-20 Q200,350 -20,400' fill='none' stroke='${userTypeColour}' stroke-width='25' opacity='0.3'/>
      <path d='M1620,200 C1200,300 1550,700 1000,920' fill='none' stroke='${userTypeColour}' stroke-width='25' opacity='0.7'/>
      <path d='M1620,450 Q1050,450 750,920' fill='none' stroke='${userTypeColour}' stroke-width='25' opacity='0.7'/>
      <path d='M1250,-20 Q1300,200 1620,250' fill='none' stroke='${userTypeColour}' stroke-width='25' opacity='0.3'/>
    </svg>
    `;
  const backgroundRibbonsImage = `url("data:image/svg+xml,${encodeURIComponent(svgContent)}")`;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editRow, setEditRow] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  // Load data from backend
  useEffect(() => {
    const fetchData = async () => {
        setLoading(true);
        const result: any = await feedbackDataRetrievalQuery({ someParam: "value" });
        setRows(result);
        setLoading(false);
    };
    fetchData();
  }, [modalOpen]);

  // Escape key exits edit mode without saving
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setEditRow(null);
        setEditValues({});
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleTableSave = async (rowIndex: number) => {
    try {
      const SUBMISSION_URI = "ezyvet/updateclient";
      const result = await postData({
        uri: SUBMISSION_URI,
        body: editValues,//rows[rowIndex], 
      });
      if("success" in result) {
        const updatedRows = [...rows];
        updatedRows[rowIndex] = { ...editValues };
        setRows(updatedRows);
        setEditRow(null);
        toast.success("Client Updated");
      } else if(result.errorMessage) {
        toast.error(result.errorMessage);
      }
        return result;
    } catch (error) {
      toast.error(error.errorMessage);
      throw error;
    }
  };

  const handleTableDelete = async (rowIndex: number) => {
    try {
      const SUBMISSION_URI = "ezyvet/removeclient";
      const result = await postData({
        uri: SUBMISSION_URI,
        body: rows[rowIndex], 
      });
      if("success" in result) {
        setRows((prev) => prev.filter((_, idx) => idx !== rowIndex));
        setEditRow(null);
        toast.success("Client Removed");
      } else if(result.errorMessage) {
        toast.error(result.errorMessage);
      }
        return result;
    } catch (error) {
      toast.error(error.errorMessage);
      throw error;
    }
  };

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  return (
  <>
    <CustomFab
      ariaLabel="open-popup"
      onClick={() => setModalOpen(true)}
      sx={{
        background: userTypeColour,
        color: "white",
        "&:hover": {
          color: userTypeColour,
        },
      }}
    >
      <Typography variant="button">Xcaliber</Typography>
    </CustomFab>

    {(userType === USER_TYPES.ADMIN || userType === USER_TYPES.DEVELOPER) && (
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          ref={modalRef}
          sx={{
            position: "absolute" as const,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            backgroundImage: backgroundRibbonsImage,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow: 24,
            p: 3,
            display: "flex",
            gap: 3,
            width: "90%",
            maxHeight: "80vh",
            overflowY: "auto",
            borderRadius: 2,
          }}
        >
          {/* Left panel – Add Client / Modality form */}
          {/*
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography
              variant="h4"
              sx={{
                color: userTypeColour,
                textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
              }}
            >
              Add Client / Modality
            </Typography>

            {fieldDefs.map(({ key, label, placeholder }) => (
              <TextField
                key={key}
                label={label}
                placeholder={placeholder}
                value={formData[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                helperText={
                  key === "none"
                    ? "(also used to identify client for deletion)"
                    : ""
                }
              />
            ))}

            <Box sx={{ display: "flex", gap: 1, paddingBottom: "20px" }}>
              <Button
                variant="contained"
                onClick={handleSubmitNewClient}
                sx={{ color: "white", backgroundColor: userTypeColour }}
              >
                Add Client / Modality
              </Button>
            </Box>
          </Box>
            */}
          {/* ✅ Right panel – read-only table */}
          <Box
            sx={{
              flex: 1.5,
              display: "flex",
              flexDirection: "column",
              height: "124vh",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: userTypeColour,
                textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
              }}
            >
              Feedback Results
            </Typography>

            {loading ? (
              <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: "100%",
                  //width: 550,
                  //overflowX: "scroll",
                  width: "100%",
                  overflowX: "auto",
                }}
              >
                <Table stickyHeader size="small" sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      {rows.length > 0 &&
                        Object.keys(rows[0]).map((col) => (
                          <TableCell key={col} sx={{ fontWeight: "bold" }}>
                            {col}
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {Object.keys(row).map((field) => (
                          <TableCell
                            key={field}
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {field === "link" ? (
                                <a
                                    href={row[field]}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: userTypeColour, textDecoration: "underline" }}
                                >
                                View Study
                                </a>
                            ) : (
                                row[field]
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </Modal>
    )}
  </>
);

};
